import styles from './OrderEconomicsPanel.module.css';
import {
  ECONOMICS_LINES,
  EconomicsRow,
  deriveProfitTone,
  formatDollars,
  formatLineSource,
  formatMargin,
  isAbsent
} from './OrderEconomicsRow';

/**
 * The stored per-order metric, rendered as the THIRD column of the economics panel — card 2459.
 *
 * 🔴 A THIRD QUANTITY, NOT A VARIANT. It shares column names with the full-term block while
 * measuring something else: purchase day plus the initial fulfillment, not the whole term. That
 * is the same reason the original snapshot is shown beside the stored economics rather than
 * merged into it, and merging any of the three would destroy the comparison the panel exists to
 * display. On a multi-month term this block's margin is legitimately HIGHER than the full-term
 * one; they are not in disagreement.
 *
 * 🔴 EVERY WORD COMES FROM THE API. The heading, the basis and the term note are read from the
 * block that `orderEconomicsView.js` publishes, which imports them from the engine that produces
 * the numbers they describe. Retyping any of them here would create a second definition free to
 * drift from the arithmetic — so this file contains no such string to compare against.
 *
 * 🔴 PURELY PRESENTATIONAL — IT DERIVES NO MONEY, and card 2460 has to reconcile these figures
 * to the cent against five other surfaces. A render layer that helpfully filled in a missing
 * line would make that impossible to audit.
 */

/** The metric's stored row carries the same lines MINUS the provider fee: migration 067 has no
 *  such column, because the metric excludes that cost by definition (`excluded_lines` records
 *  the ruling on every row). Derived by subtraction rather than hand-written, so the two lists
 *  can never fall into different orders. */
const METRIC_LINES = Object.freeze(ECONOMICS_LINES.filter(({ key }) => key !== 'providerFee'));

/** `isInitialOrder` is THREE-state, and the three are three different facts. `067:123` declares
 *  `is_initial_order boolean NOT NULL`, so a STORED row is strictly yes or no; `null` reaches us
 *  only when nothing was stored at all. "no" and "we never recorded one" must not share a
 *  sentence — `false` is a measurement, `null` is the absence of one. */
const INITIAL_ORDER_TEXT = Object.freeze({
  yes: 'Initial order: yes',
  no: 'Initial order: no — renewal',
  unknown: 'Initial order: not recorded'
});

/**
 * @param {object} props.block  the `firstOrderValue` block from `/admin/orders/:id/detail`:
 *   `{ isRecorded, value, isInitialOrder, label, basis, termNote, scope, exclusions, version }`.
 *   `value` is null when nothing was recorded and MUST render as a stated absence, never as
 *   zeros — exactly as the full-term block does for `economics: null`.
 */
const OrderFirstOrderValueSection = ({ block }) => {
  const value = block?.value ?? null;
  const isRecorded = Boolean(block?.isRecorded && value);
  const isInitial = block?.isInitialOrder ?? null;
  const flag = !isRecorded ? 'unknown' : (isInitial === true ? 'yes' : 'no');
  const lineSources = value?.lineSources ?? {};

  return (
    <div className={styles.column} data-testid="first-order-value-section">
      <p className={styles.sectionLabel} data-testid="first-order-value-label">
        {block?.label}{isRecorded ? ` · v${block.version}` : ''}
      </p>

      <p
        className={styles.initialFlag}
        data-testid="first-order-value-initial-flag"
        data-initial-order={flag}
      >
        {INITIAL_ORDER_TEXT[flag]}
      </p>

      {flag === 'yes' && (
        <div data-testid="first-order-value-lines">
          {METRIC_LINES.map(({ key, sourceKey, label, emphasis }) => (
            <EconomicsRow
              key={key}
              testId={`fov-${sourceKey}`}
              label={label}
              amount={formatDollars(value[key])}
              quality={lineSources?.[sourceKey]?.quality}
              source={formatLineSource(lineSources?.[sourceKey])}
              emphasis={emphasis}
              tone={key === 'profit' ? deriveProfitTone(value[key]) : null}
            />
          ))}
          <EconomicsRow
            testId="fov-margin"
            label="Margin"
            amount={formatMargin(value.margin)}
            quality={value.dataQuality}
            source={isAbsent(value.margin)
              ? 'undefined when net revenue is 0'
              : 'derived · profit / net revenue'}
            tone={deriveProfitTone(value.profit)}
          />
          <EconomicsRow
            testId="fov-shipments"
            label="Shipments counted"
            amount={isAbsent(value.shipments) ? null : String(value.shipments)}
            source={formatLineSource(lineSources?.shipments)}
          />
        </div>
      )}

      {/* 🔴 A RENEWAL IS SHOWN WITHOUT A FIGURE, DELIBERATELY. Its row exists — `067:159` stores
          the predicate on EVERY order so that "excludes renewals" is an auditable WHERE clause
          rather than a comment — but the metric counts first orders only, and every aggregate
          filters `is_initial_order = true`. Printing a renewal's purchase-day money under this
          heading would assert something the metric does not mean. */}
      {flag === 'no' && (
        <div className={styles.notice} data-testid="first-order-value-renewal">
          Recorded, but not a first order
          <span className={styles.noticeHint}>
            This order is a renewal. Its purchase-day figures are stored for audit and are
            excluded wherever this metric is aggregated, so no amount is shown here.
          </span>
        </div>
      )}

      {flag === 'unknown' && (
        <div className={styles.notice} data-testid="first-order-value-not-recorded">
          Not recorded
          <span className={styles.noticeHint}>
            This order has no stored <code>order_first_order_value</code> row, so there is no
            figure to show. This is an absence, not a zero.
          </span>
        </div>
      )}

      {block?.basis && <p className={styles.basisNote}>{block.basis}</p>}
      {block?.termNote && <p className={styles.termNote}>{block.termNote}</p>}
    </div>
  );
};

export default OrderFirstOrderValueSection;
